import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AppSettings {
  // UI Settings
  theme: 'light' | 'dark' | 'auto';
  language: string;
  timezone: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  
  // Layout Settings
  sidebarPosition: 'left' | 'right';
  sidebarCollapsed: boolean;
  headerSticky: boolean;
  footerVisible: boolean;
  
  // Notification Settings
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
    desktop: boolean;
    sound: boolean;
  };
  
  // Privacy Settings
  privacy: {
    dataCollection: boolean;
    analytics: boolean;
    marketing: boolean;
    thirdParty: boolean;
  };
  
  // Performance Settings
  performance: {
    lazyLoading: boolean;
    imageOptimization: boolean;
    caching: boolean;
    compression: boolean;
  };
  
  // Accessibility Settings
  accessibility: {
    highContrast: boolean;
    largeText: boolean;
    reducedMotion: boolean;
    screenReader: boolean;
  };
}

interface SettingsState {
  settings: AppSettings;
  isLoading: boolean;
  error: string | null;
}

interface SettingsActions {
  // Settings management
  updateSettings: (updates: Partial<AppSettings>) => void;
  updateSetting: <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => void;
  resetSettings: () => void;
  resetSection: (section: keyof AppSettings) => void;
  
  // State management
  setSettings: (settings: AppSettings) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  
  // Utility methods
  getSetting: <K extends keyof AppSettings>(key: K) => AppSettings[K];
  exportSettings: () => string;
  importSettings: (settingsJson: string) => boolean;
}

const defaultSettings: AppSettings = {
  // UI Settings
  theme: 'auto',
  language: 'en',
  timezone: 'UTC',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  
  // Layout Settings
  sidebarPosition: 'left',
  sidebarCollapsed: false,
  headerSticky: true,
  footerVisible: true,
  
  // Notification Settings
  notifications: {
    email: true,
    push: true,
    sms: false,
    desktop: true,
    sound: true,
  },
  
  // Privacy Settings
  privacy: {
    dataCollection: true,
    analytics: true,
    marketing: false,
    thirdParty: false,
  },
  
  // Performance Settings
  performance: {
    lazyLoading: true,
    imageOptimization: true,
    caching: true,
    compression: true,
  },
  
  // Accessibility Settings
  accessibility: {
    highContrast: false,
    largeText: false,
    reducedMotion: false,
    screenReader: false,
  },
};

export const useSettingsStore = create<SettingsState & SettingsActions>()(
  persist(
    (set, get) => ({
      // Initial state
      settings: defaultSettings,
      isLoading: false,
      error: null,

      // Settings management
      updateSettings: (updates) => {
        set((state) => ({
          settings: { ...state.settings, ...updates }
        }));
      },

      updateSetting: (key, value) => {
        set((state) => ({
          settings: { ...state.settings, [key]: value }
        }));
      },

      resetSettings: () => {
        set({ settings: defaultSettings });
      },

      resetSection: (section) => {
        set((state) => ({
          settings: { ...state.settings, [section]: defaultSettings[section] }
        }));
      },

      // State management
      setSettings: (settings) => set({ settings }),
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      // Utility methods
      getSetting: (key) => {
        return get().settings[key];
      },

      exportSettings: () => {
        const settings = get().settings;
        return JSON.stringify(settings, null, 2);
      },

      importSettings: (settingsJson) => {
        try {
          const parsedSettings = JSON.parse(settingsJson);
          
          // Validate the imported settings
          if (typeof parsedSettings === 'object' && parsedSettings !== null) {
            set({ settings: { ...defaultSettings, ...parsedSettings } });
            return true;
          }
          return false;
        } catch (error) {
          set({ error: 'Invalid settings format' });
          return false;
        }
      }
    }),
    {
      name: 'app-settings',
      partialize: (state) => ({
        settings: state.settings
      })
    }
  )
);


